import { apiFetch } from "./client";

/**
 * Plan identifiers as the API stores them. "free" is displayed as
 * "Community"; the other three match their display names. Legacy "pro" rows
 * are normalised to "growth" by the API before they reach the dashboard.
 */
export type PlanName = "free" | "starter" | "growth" | "enterprise";
export type PaidPlanName = Exclude<PlanName, "free">;

export const PLAN_ORDER: PlanName[] = ["free", "starter", "growth", "enterprise"];

export interface PlanLimits {
  displayName: string;
  priceMonthlyCents: number;
  contract: string;
  bestFor: string;
  maxRepos: number;
  maxScansPerMonth: number;
  maxMembers: number;
  maxUploadBytes: number;
  features: string[];
}

export interface BillingPlan extends PlanLimits {
  price: { monthly: number; annual: number };
  /** A self-serve checkout exists for this plan with the configured provider. */
  purchasable: boolean;
}

export interface BillingConfig {
  enabled: boolean;
  provider?: "paystack" | "gumroad";
  /** "one_time" = single payment grants N days of access; "subscription" = recurring. */
  billingModel?: "subscription" | "one_time";
  /** Days of access granted by a one-time purchase (null for subscription billing). */
  oneTimeDays?: number | null;
  publicKey: string | null;
  planOrder: PlanName[];
  contactEmail: string;
  plans: Record<PlanName, BillingPlan>;
  featureDisplay: Record<string, string>;
}

export interface SubscriptionInfo {
  plan: PlanName;
  limits: PlanLimits;
  subscription: {
    status: string;
    currentPeriodEnd: string | null;
    trialEndsAt: string | null;
    cancelAtPeriodEnd: boolean;
    planCode: string | null;
    billingEmail: string | null;
    hasCustomer: boolean;
  };
}

export interface UsageInfo {
  plan: PlanName;
  limits: PlanLimits;
  usage: {
    repos: { current: number; limit: number };
    scansThisMonth: { current: number; limit: number };
    members: { current: number; limit: number };
  };
}

export interface Invoice {
  id: string;
  status: string;
  currency: string;
  amount_due: number;
  amount_paid: number;
  invoice_url: string | null;
  period_start: string;
  period_end: string;
  created_at: string;
  paid_at: string | null;
}

/** Customer-facing name for a plan id, with a safe fallback before config loads. */
export function planDisplayName(plan: PlanName | string | null | undefined, config?: BillingConfig | null): string {
  const fromConfig = config?.plans?.[plan as PlanName]?.displayName;
  if (fromConfig) return fromConfig;
  switch (plan) {
    case "free": return "Community";
    case "starter": return "Starter";
    case "growth": return "Growth";
    case "enterprise": return "Enterprise";
    default: return "Community";
  }
}

export function formatLimit(n: number): string {
  return n === -1 ? "Unlimited" : String(n);
}

export function formatUploadCap(bytes: number): string {
  if (bytes === -1) return "Unlimited";
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

/** mailto: link for plans sold through sales rather than self-serve checkout. */
export function salesMailto(plan: PlanName, contactEmail: string): string {
  const subject = plan === "enterprise" ? "Paxley Enterprise" : `Paxley ${planDisplayName(plan)}`;
  return `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}`;
}

export async function getBillingConfig(): Promise<BillingConfig> {
  return apiFetch<BillingConfig>({ path: "/v1/billing/config" });
}

export async function getSubscription(): Promise<SubscriptionInfo> {
  return apiFetch<SubscriptionInfo>({ path: "/v1/billing/subscription" });
}

export async function getUsage(): Promise<UsageInfo> {
  return apiFetch<UsageInfo>({ path: "/v1/billing/usage" });
}

export async function getInvoices(): Promise<{ invoices: Invoice[] }> {
  return apiFetch<{ invoices: Invoice[] }>({ path: "/v1/billing/invoices" });
}

export async function createCheckout(plan: PaidPlanName, interval: "monthly" | "annual"): Promise<{ url: string; reference: string }> {
  return apiFetch<{ url: string; reference: string }>({ method: "POST", path: "/v1/billing/checkout", body: { plan, interval } });
}

export async function createPortalSession(): Promise<{ url: string }> {
  return apiFetch<{ url: string }>({ method: "POST", path: "/v1/billing/portal" });
}

export async function verifyCheckout(reference: string): Promise<{ verified: boolean; status: string }> {
  return apiFetch<{ verified: boolean; status: string }>({ method: "POST", path: "/v1/billing/verify", body: { reference } });
}
